import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { UserPlus, X, Check, Users, AlertCircle, Key, User, Eye, EyeOff } from 'lucide-react'

interface Student {
  id: number
  name: string
  canvas_id: number
  is_linked: boolean
}

interface LinkedStudent {
  student_id: number
  student_name: string
  canvas_id: number
  linked_at: string
  last_synced: string | null
}

interface StudentAccount {
  id: number
  name: string
  canvas_id: number
  has_account: boolean
  username: string | null
  account_created_at: string | null
  last_login: string | null
  is_active: boolean | null
}

export default function ManageChildren() {
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [showAccountModal, setShowAccountModal] = useState<StudentAccount | null>(null)
  const [showPasswordModal, setShowPasswordModal] = useState<StudentAccount | null>(null)
  const queryClient = useQueryClient()

  // Form states
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pin, setPin] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState('')

  // Fetch linked students
  const { data: linkedData, isLoading: linkedLoading } = useQuery({
    queryKey: ['linked-students'],
    queryFn: async () => {
      const res = await api.get('/api/parent/linked-students')
      return res.data
    },
  })

  // Fetch student accounts
  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['student-accounts'],
    queryFn: async () => {
      const res = await api.get('/api/parent/students/accounts')
      return res.data
    },
  })

  // Fetch available students to link
  const { data: availableData, isLoading: availableLoading } = useQuery({
    queryKey: ['available-students'],
    queryFn: async () => {
      const res = await api.get('/api/parent/available-students')
      return res.data
    },
    enabled: showLinkModal,
  })

  // Link student mutation
  const linkMutation = useMutation({
    mutationFn: async (studentId: number) => {
      await api.post('/api/parent/link-student', { student_id: studentId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linked-students'] })
      queryClient.invalidateQueries({ queryKey: ['available-students'] })
      queryClient.invalidateQueries({ queryKey: ['student-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['auth-user'] })
    },
  })

  // Unlink student mutation
  const unlinkMutation = useMutation({
    mutationFn: async (studentId: number) => {
      await api.delete(`/api/parent/unlink-student/${studentId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linked-students'] })
      queryClient.invalidateQueries({ queryKey: ['available-students'] })
      queryClient.invalidateQueries({ queryKey: ['student-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['auth-user'] })
    },
  })

  // Create account mutation
  const createAccountMutation = useMutation({
    mutationFn: async ({ studentId, username, password, pin }: { studentId: number; username: string; password: string; pin?: string }) => {
      const res = await api.post(`/api/parent/students/${studentId}/account`, {
        username,
        password,
        pin: pin || undefined,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-accounts'] })
      setShowAccountModal(null)
      resetForm()
    },
    onError: (error: { response?: { data?: { error?: string } } }) => {
      setFormError(error.response?.data?.error || 'Failed to create account')
    },
  })

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ studentId, password, pin }: { studentId: number; password: string; pin?: string }) => {
      const res = await api.put(`/api/parent/students/${studentId}/account/password`, {
        password,
        pin: pin || undefined,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-accounts'] })
      setShowPasswordModal(null)
      resetForm()
    },
    onError: (error: { response?: { data?: { error?: string } } }) => {
      setFormError(error.response?.data?.error || 'Failed to reset password')
    },
  })

  const resetForm = () => {
    setUsername('')
    setPassword('')
    setConfirmPassword('')
    setPin('')
    setShowPassword(false)
    setFormError('')
  }

  const openAccountModal = (student: StudentAccount) => {
    resetForm()
    // Suggest username from name
    const suggestedUsername = student.name.toLowerCase().replace(/\s+/g, '').slice(0, 15)
    setUsername(suggestedUsername)
    setShowAccountModal(student)
  }

  const openPasswordModal = (student: StudentAccount) => {
    resetForm()
    setShowPasswordModal(student)
  }

  const handleCreateAccount = () => {
    setFormError('')

    if (!username || username.length < 3) {
      setFormError('Username must be at least 3 characters')
      return
    }

    if (!password || password.length < 4) {
      setFormError('Password must be at least 4 characters')
      return
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match')
      return
    }

    if (pin && (!/^\d{4}$/.test(pin))) {
      setFormError('PIN must be exactly 4 digits')
      return
    }

    if (showAccountModal) {
      createAccountMutation.mutate({
        studentId: showAccountModal.id,
        username,
        password,
        pin: pin || undefined,
      })
    }
  }

  const handleResetPassword = () => {
    setFormError('')

    if (!password || password.length < 4) {
      setFormError('Password must be at least 4 characters')
      return
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match')
      return
    }

    if (pin && (!/^\d{4}$/.test(pin))) {
      setFormError('PIN must be exactly 4 digits')
      return
    }

    if (showPasswordModal) {
      resetPasswordMutation.mutate({
        studentId: showPasswordModal.id,
        password,
        pin: pin || undefined,
      })
    }
  }

  const linkedStudents: LinkedStudent[] = linkedData?.linked_students || []
  const studentAccounts: StudentAccount[] = accountsData?.students || []
  const availableStudents: Student[] = availableData?.students || []
  const unlinkedStudents = availableStudents.filter(s => !s.is_linked)

  // Merge linked students with account info
  const studentsWithAccounts = linkedStudents.map(linked => {
    const account = studentAccounts.find(a => a.id === linked.student_id)
    return {
      ...linked,
      has_account: account?.has_account || false,
      username: account?.username || null,
      last_login: account?.last_login || null,
      is_active: account?.is_active ?? null,
      account_created_at: account?.account_created_at || null,
    }
  })

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Manage Children</h1>
          <p className="text-gray-400 mt-1">Link children and manage their login accounts</p>
        </div>
        <button
          onClick={() => setShowLinkModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <UserPlus className="w-5 h-5" />
          Add Child
        </button>
      </div>

      {/* Linked children */}
      {linkedLoading || accountsLoading ? (
        <div className="card animate-pulse">
          <div className="h-20 bg-gray-700 rounded"></div>
        </div>
      ) : studentsWithAccounts.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Children Linked</h3>
          <p className="text-gray-400 mb-6">
            Link your children's accounts to see their homework, grades, and achievements.
          </p>
          <button
            onClick={() => setShowLinkModal(true)}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <UserPlus className="w-5 h-5" />
            Add Your First Child
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {studentsWithAccounts.map((student) => (
            <div key={student.student_id} className="card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-quest-primary/20 rounded-full flex items-center justify-center">
                    <span className="text-xl font-bold text-quest-primary">
                      {student.student_name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold">{student.student_name}</h3>
                    <p className="text-sm text-gray-400">
                      Linked {new Date(student.linked_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirm(`Remove ${student.student_name} from your account?`)) {
                      unlinkMutation.mutate(student.student_id)
                    }
                  }}
                  disabled={unlinkMutation.isPending}
                  className="text-gray-400 hover:text-red-400 transition-colors p-2"
                  title="Remove child"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Account status section */}
              <div className="mt-4 pt-4 border-t border-gray-700/50">
                {student.has_account ? (
                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-900/30 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-400">Account Active</p>
                      <p className="text-xs text-gray-400">
                        Username: <span className="font-mono text-gray-300">{student.username}</span>
                        {student.last_login && (
                          <span className="ml-2">
                            · Last login: {new Date(student.last_login).toLocaleDateString()}
                          </span>
                        )}
                        <span className="ml-2">
                          · Last synced: {student.last_synced ? new Date(student.last_synced).toLocaleString() : 'Never'}
                        </span>
                      </p>
                    </div>
                  </div>
                    <button
                      onClick={() => openPasswordModal({
                        id: student.student_id,
                        name: student.student_name,
                        canvas_id: student.canvas_id,
                        has_account: true,
                        username: student.username,
                        account_created_at: student.account_created_at,
                        last_login: student.last_login,
                        is_active: student.is_active,
                      })}
                      className="btn btn-sm bg-gray-700 hover:bg-gray-600 flex items-center gap-2"
                    >
                      <Key className="w-4 h-4" />
                      Reset Password
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-900/30 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-yellow-400">No Login Account</p>
                      <p className="text-xs text-gray-400">
                        Create an account so {student.student_name.split(' ')[0]} can log in
                        <span className="ml-2">
                          · Last synced: {student.last_synced ? new Date(student.last_synced).toLocaleString() : 'Never'}
                        </span>
                      </p>
                    </div>
                  </div>
                    <button
                      onClick={() => openAccountModal({
                        id: student.student_id,
                        name: student.student_name,
                        canvas_id: student.canvas_id,
                        has_account: false,
                        username: null,
                        account_created_at: null,
                        last_login: null,
                        is_active: null,
                      })}
                      className="btn btn-sm btn-primary flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Create Account
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info box */}
      <div className="mt-8 p-4 bg-blue-900/20 border border-blue-800 rounded-lg flex gap-3">
        <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-300">
          <p className="font-medium text-blue-400 mb-1">How does linking work?</p>
          <p>
            Children must first be synced from Canvas LMS before they can be linked.
            Once linked, you can create a login account so they can access the student portal
            with their own username and password.
          </p>
        </div>
      </div>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Add Child</h2>
              <button
                onClick={() => setShowLinkModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-96">
              {availableLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-gray-700 rounded animate-pulse" />
                  ))}
                </div>
              ) : unlinkedStudents.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">
                    {availableStudents.length === 0
                      ? 'No students found in the system. Students must be synced from Canvas first.'
                      : 'All available students are already linked to your account.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-400 mb-3">
                    Select your child to link them to your account:
                  </p>
                  {unlinkedStudents.map((student) => (
                    <button
                      key={student.id}
                      onClick={() => {
                        linkMutation.mutate(student.id)
                        setShowLinkModal(false)
                      }}
                      disabled={linkMutation.isPending}
                      className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-between transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-quest-primary/20 rounded-full flex items-center justify-center">
                          <span className="font-bold text-quest-primary">
                            {student.name.charAt(0)}
                          </span>
                        </div>
                        <span className="font-medium">{student.name}</span>
                      </div>
                      <Check className="w-5 h-5 text-gray-500" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-700/50">
              <button
                onClick={() => setShowLinkModal(false)}
                className="w-full btn bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      {showAccountModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card rounded-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Create Account for {showAccountModal.name}</h2>
              <button
                onClick={() => { setShowAccountModal(null); resetForm() }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {formError && (
                <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-400 text-sm">
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
                  placeholder="Enter username"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-quest-primary"
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-400">
                  This is what they'll use to log in (min 3 characters)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-quest-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Minimum 4 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-quest-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Quick PIN (Optional)
                </label>
                <input
                  type="text"
                  value={pin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                    setPin(val)
                  }}
                  placeholder="4-digit PIN"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-quest-primary font-mono tracking-widest"
                  maxLength={4}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Optional 4-digit PIN for quick login
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-700/50 flex gap-3">
              <button
                onClick={() => { setShowAccountModal(null); resetForm() }}
                className="flex-1 btn bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAccount}
                disabled={createAccountMutation.isPending}
                className="flex-1 btn btn-primary"
              >
                {createAccountMutation.isPending ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card rounded-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-700/50 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Reset Password for {showPasswordModal.name}</h2>
              <button
                onClick={() => { setShowPasswordModal(null); resetForm() }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {formError && (
                <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-400 text-sm">
                  {formError}
                </div>
              )}

              <div className="p-3 bg-gray-700/50 rounded-lg">
                <p className="text-sm text-gray-300">
                  Current username: <span className="font-mono text-white">{showPasswordModal.username}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-quest-primary"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Minimum 4 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Confirm New Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-quest-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Update Quick PIN (Optional)
                </label>
                <input
                  type="text"
                  value={pin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                    setPin(val)
                  }}
                  placeholder="Leave blank to keep current PIN"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-quest-primary font-mono tracking-widest"
                  maxLength={4}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Leave blank to keep current PIN, or enter new 4-digit PIN
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-700/50 flex gap-3">
              <button
                onClick={() => { setShowPasswordModal(null); resetForm() }}
                className="flex-1 btn bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resetPasswordMutation.isPending}
                className="flex-1 btn btn-primary"
              >
                {resetPasswordMutation.isPending ? 'Updating...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
